import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Spinner, ArrowLeft, Clock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "loading", data: null });
  // phases: loading | paid | processing | failed | not_found

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "not_found", data: null });
      return;
    }
    let attempts = 0;
    const maxAttempts = 6;
    const interval = 2500;
    let cancelled = false;

    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        attempts += 1;
        const ps = r.data.payment_status;
        const st = r.data.status;

        if (ps === "paid" || st === "complete") {
          setState({ phase: "paid", data: r.data });
          return;
        }
        if (st === "expired" || ps === "failed") {
          setState({ phase: "failed", data: r.data });
          return;
        }
        if (attempts >= maxAttempts) {
          // Likely payment is processing / webhook hasn't fired yet
          setState({ phase: "processing", data: r.data });
          return;
        }
        setTimeout(poll, interval);
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 404) {
          setState({ phase: "not_found", data: null });
          return;
        }
        if (attempts < maxAttempts) {
          attempts += 1;
          setTimeout(poll, interval);
        } else {
          setState({ phase: "processing", data: null });
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const amount = state.data?.amount_total ? (state.data.amount_total / 100).toFixed(2) : null;
  const currency = String(state.data?.currency || "usd").toUpperCase();
  const pkg = state.data?.metadata?.package_label;

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl border border-zinc-200 p-8 sm:p-10 text-center shadow-xl">
          {state.phase === "loading" && (
            <>
              <Spinner size={48} weight="duotone" className="mx-auto text-blue-600 animate-spin" />
              <h1 className="mt-5 font-heading text-2xl font-bold text-zinc-900">Confirming payment…</h1>
              <p className="mt-2 text-zinc-600">This should take just a moment.</p>
            </>
          )}

          {state.phase === "paid" && (
            <>
              <CheckCircle size={56} weight="duotone" className="mx-auto text-blue-600" />
              <h1 className="mt-5 font-heading text-3xl font-bold text-zinc-900">Payment received.</h1>
              <p className="mt-3 text-zinc-600">
                Thanks! We've received your payment of{" "}
                <span className="font-semibold text-zinc-900">${amount} {currency}</span>.
                We'll follow up with next steps by email.
              </p>
              {pkg && (
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5 text-sm text-blue-700">
                  {pkg}
                </div>
              )}
            </>
          )}

          {state.phase === "processing" && (
            <>
              <Clock size={56} weight="duotone" className="mx-auto text-blue-600" />
              <h1 className="mt-5 font-heading text-3xl font-bold text-zinc-900">Thanks — we're on it.</h1>
              <p className="mt-3 text-zinc-600">
                Your order is being processed. Confirmation can take a few minutes to appear. We'll email you as soon as it clears.
              </p>
              {pkg && amount && (
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5 text-sm text-blue-700">
                  {pkg} · ${amount} {currency}
                </div>
              )}
            </>
          )}

          {state.phase === "failed" && (
            <>
              <XCircle size={56} weight="duotone" className="mx-auto text-red-500" />
              <h1 className="mt-5 font-heading text-2xl font-bold text-zinc-900">Payment didn't go through</h1>
              <p className="mt-3 text-zinc-600">
                Your card wasn't charged. Try again, or contact us and we'll help sort it out.
              </p>
            </>
          )}

          {state.phase === "not_found" && (
            <>
              <XCircle size={56} weight="duotone" className="mx-auto text-zinc-400" />
              <h1 className="mt-5 font-heading text-2xl font-bold text-zinc-900">Session not found</h1>
              <p className="mt-3 text-zinc-600">
                We couldn't find this payment session. If you were charged, please email us.
              </p>
            </>
          )}

          <Link
            to="/"
            data-testid="payment-back-home"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white px-6 py-3 font-semibold transition-colors"
          >
            <ArrowLeft size={16} weight="bold" /> Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
